import React from "react";
import classNames from "classnames";

import "./Alert.scss";
import { Overlay } from "../Overlay/Overlay";
import { Svg, SVG_ICONS } from "../Svg/Svg";
import { Typography } from "../Typography/Typography";
import { useEffect } from "react";
import { TTSButton } from "../_controls";
import { useTranslation } from "react-i18next";

/**
 * Interface for Alert sub-components.
 *
 * @description Defines the sub-components available on the Alert component.
 */
interface AlertSubComponents {
  Text: React.FC<TextProps>;
  SubText: React.FC<SubTextProps>;
  Icon: React.FC<IconProps>;
  Footer: React.FC<FooterProps>;
}

/**
 * Props for the Alert Text component.
 *
 * @description The Text component displays the main text content of an alert.
 *
 * @property {string | React.ReactNode} [children] - The text content to display.
 * @property {string} [className] - Additional CSS class names.
 *
 * @example
 * <Alert.Text>Important message</Alert.Text>
 */
interface TextProps {
  children?: string | React.ReactNode;
  className?: string;
}

/**
 * Props for the Alert SubText component.
 *
 * @description The SubText component displays secondary text content of an alert.
 *
 * @property {string | React.ReactNode} [children] - The sub-text content to display.
 * @property {string} [className] - Additional CSS class names.
 *
 * @example
 * <Alert.SubText>Additional details here</Alert.SubText>
 */
interface SubTextProps {
  children?: string | React.ReactNode;
  className?: string;
}

/**
 * Props for the Alert Icon component.
 *
 * @description The Icon component displays an icon in the alert.
 *
 * @property {string} [icon] - Name of the icon to display.
 * @property {number} [width=64] - Width of the icon in pixels.
 * @property {number} [height=64] - Height of the icon in pixels.
 * @property {string} [className] - Additional CSS class names.
 *
 * @example
 * <Alert.Icon icon="warning" width={48} height={48} />
 */
interface IconProps {
  icon?: string;
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Props for the Alert Footer component.
 *
 * @description The Footer component displays action buttons at the bottom of the alert.
 *
 * @property {React.ReactNode} [children] - Content to render (usually buttons).
 * @property {string} [className] - Additional CSS class names.
 *
 * @example
 * <Alert.Footer>
 *   <Button title="Cancel" onClick={handleClose} />
 *   <Button title="Confirm" type="primary" onClick={handleConfirm} />
 * </Alert.Footer>
 */
interface FooterProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * Props for the Alert component.
 *
 * @description The Alert component is a modal alert dialog with overlay,
 * title, and customizable content. It uses a compound component pattern.
 *
 * @property {string} [title] - The title text displayed in the alert header.
 * @property {boolean} [show=false] - Whether the alert is visible.
 * @property {React.ReactNode} [children] - Alert content (Text, SubText, Icon, Footer components).
 * @property {string} [wrapperClassname] - Additional CSS class names for the wrapper.
 * @property {React.ReactNode} [customClose] - Custom close button element.
 *   If not provided, a default close button is rendered.
 * @property {() => void} [onHide] - Function called when the close button is clicked.
 * @property {string} [onHideTestId] - Data-testid attribute for the close button.
 * @property {string} [ttsButtonId] - HTML id attribute for the TTS button.
 * @property {string} [id] - HTML id attribute for the alert wrapper.
 * @property {number} [tabIndex] - Tab index for keyboard navigation.
 * @property {boolean} [isDialogModal=false] - Whether the alert is used as a dialog modal.
 *   When true, the TTS button is not shown in the header.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic alert
 * <Alert show={isOpen} title="Warning" onHide={() => setIsOpen(false)}>
 *   <Alert.Text>Are you sure?</Alert.Text>
 *   <Alert.SubText>This action cannot be undone.</Alert.SubText>
 *   <Alert.Footer>
 *     <Button title="Cancel" onClick={() => setIsOpen(false)} />
 *     <Button title="Confirm" type="primary" onClick={handleConfirm} />
 *   </Alert.Footer>
 * </Alert>
 *
 * @example
 * // Alert with icon
 * <Alert show={isOpen} title="Success" onHide={() => setIsOpen(false)}>
 *   <Alert.Icon icon="check-circle" />
 *   <Alert.Text>Operation completed successfully</Alert.Text>
 * </Alert>
 */
interface Props {
  title?: string;
  show?: boolean;
  children?: React.ReactNode;
  wrapperClassname?: string;
  customClose?: React.ReactNode;
  onHide?: () => void;
  onHideTestId?: string;
  ttsButtonId?: string;
  id?: string;
  tabIndex?: number;
  isDialogModal?: boolean;
  testId?: string;
}

/**
 * A modal alert component with composable sub-components.
 *
 * @description Alert renders a modal alert dialog that appears on top of the page content.
 * It uses a compound component pattern with Text, SubText, Icon, and Footer sub-components
 * for flexible composition. The alert automatically manages body scroll locking when open.
 *
 * Features:
 * - Compound component pattern for flexible composition
 * - Automatic body scroll lock when open
 * - Overlay background
 * - Optional TTS (Text-to-Speech) button
 * - Custom close button support
 * - Typography component integration
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} An alert dialog with overlay
 *
 * @see Props for all available properties
 * @see Text for alert text component
 * @see SubText for alert sub-text component
 * @see Icon for alert icon component
 * @see Footer for alert footer component
 */
const Alert: React.FC<Props> & AlertSubComponents = (props) => {
  const { t } = useTranslation();
  const {
    show = false,
    children,
    wrapperClassname,
    title,
    customClose,
    ttsButtonId,
    id,
    tabIndex,
    testId,
    onHideTestId,
    isDialogModal = false,
  } = props;

  const wrapClass = classNames(
    "alert-wrapper",
    { open: !!show },
    wrapperClassname
  );

  /**
   * Manages body scroll lock when alert is open.
   * Adds 'alert-open' class to prevent background scrolling.
   * Cleans up by removing the class when alert closes or unmounts.
   */
  useEffect(
    function manageBodyScrollLock() {
      const body = document.body;
      if (show) {
        body.classList.add("alert-open");
      } else {
        body.classList.remove("alert-open");
      }
    },
    [show]
  );

  return (
    <div className={wrapClass} id={id} tabIndex={tabIndex} data-testid={testId}>
      <Overlay open={show} />
      <div className="alert-content">
        <div className="alert">
          <div className="alert-header">
            <Typography
              variant="title2"
              component="div"
              className="alert-header__title"
            >
              {title}
              {title && !isDialogModal && (
                <TTSButton
                  value={title}
                  className="tts-button-modal"
                  id={ttsButtonId}
                />
              )}
            </Typography>
            {customClose ? (
              customClose
            ) : (
              <button
                data-testid={onHideTestId}
                className="alert-header__close__btn"
                onClick={props.onHide}
                aria-label="alert-header-close-btn"
              >
                <Svg
                  icon={SVG_ICONS.DRAWER_CLOSE}
                  width={24}
                  height={24}
                  title={t("PRIVACY_COMMON_CLOSE_ALT_TEXT")}
                />
              </button>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * The main text component for an Alert.
 *
 * @description Text renders the primary text content of an alert using
 * Typography component with title2 variant.
 *
 * @param {TextProps} props - The component props
 * @returns {JSX.Element} A paragraph element with alert text styling
 *
 * @see TextProps for all available properties
 */
const Text: React.FC<TextProps> = (props) => {
  return (
    <Typography
      className={classNames("alert-text", props.className)}
      variant="title2"
      component="p"
    >
      {props.children}
    </Typography>
  );
};

/**
 * The sub-text component for an Alert.
 *
 * @description SubText renders secondary text content of an alert using
 * Typography component with flag variant.
 *
 * @param {SubTextProps} props - The component props
 * @returns {JSX.Element} A paragraph element with alert sub-text styling
 *
 * @see SubTextProps for all available properties
 */
const SubText: React.FC<SubTextProps> = (props) => {
  return (
    <Typography
      className={classNames("alert-sub-text", props.className)}
      variant="flag"
      component="p"
    >
      {props.children}
    </Typography>
  );
};

/**
 * The icon component for an Alert.
 *
 * @description Icon renders an icon in the alert using the Svg component.
 * Defaults to 64x64 pixel size.
 *
 * @param {IconProps} props - The component props
 * @returns {JSX.Element} A div containing the icon
 *
 * @see IconProps for all available properties
 */
const Icon: React.FC<IconProps> = (props) => {
  const { icon = "", width = 64, height = 64 } = props;

  return (
    <div className="alert-icon">
      <Svg icon={icon} width={width} height={height} />
    </div>
  );
};

/**
 * The footer component for an Alert.
 *
 * @description Footer renders the bottom section of the alert, typically used
 * for action buttons.
 *
 * @param {FooterProps} props - The component props
 * @returns {JSX.Element} A div for alert actions
 *
 * @see FooterProps for all available properties
 */
const Footer: React.FC<FooterProps> = (props) => (
  <div className="alert-footer">{props.children}</div>
);

Alert.Text = Text;
Alert.SubText = SubText;
Alert.Icon = Icon;
Alert.Footer = Footer;

export default Alert;
